/**
 * Engagement Phases view (handoff §5.4).
 *
 * A four-phase rail plus 2×2 phase cards listing the LD-native deliverables Stemmler hands to
 * SMBC. §5.4: "it is not a project-management task tracker" — no assignment, no status edits.
 *
 * §4.6: "Only two states allow download: Ready and Draft. Queued and Planned render a disabled
 * 'Not yet' button."
 */

import { useState } from 'react';
import { Download } from 'lucide-react';
import { color, font, radius, shadow, formatChip } from '../tokens';
import type { PhaseDef, EngagementArtifact } from '../types';
import { isDownloadable } from '../types';

const RAIL_COLOR = { complete: '#16a34a', active: '#f59e0b', queued: '#c9c8d9' } as const;

interface Props {
  phases: PhaseDef[];
  onToast: (msg: string) => void;
  /**
   * Item 14 — wired to the real /api/engagement/artifacts/:id/download route. Returns a blob
   * object URL + the real filename (Content-Disposition), NOT a plain URL for `window.location`:
   * auth on this app goes through apiClient's Authorization header (api.ts), which a raw browser
   * navigation wouldn't carry, so the download has to go through an authenticated fetch first —
   * same pattern as ArtifactPanel.tsx's downloadText and useAttestation.ts's exportPack.
   */
  download?: (artifact: EngagementArtifact) => Promise<{ url: string; filename: string }>;
}

export default function Phases({ phases, onToast, download }: Props) {
  const [phasePick, setPhasePick] = useState<1 | 2 | 3 | 4>(1);

  const onDownload = async (a: EngagementArtifact) => {
    if (!isDownloadable(a.state)) return;
    if (!download) {
      onToast('Download endpoint not wired yet');
      return;
    }
    try {
      const { url, filename } = await download(a);
      onToast('Download started');
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      onToast('Could not start the download');
    }
  };

  return (
    <div>
      {/* Progress rail */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 22 }}>
        {phases.map((p, i) => (
          <div key={p.id} style={{ display: 'flex', alignItems: 'center', flex: i === phases.length - 1 ? '0 0 auto' : 1 }}>
            <button
              onClick={() => setPhasePick(p.id)}
              aria-label={`Phase ${p.id} — ${p.title}`}
              style={{
                width: 34, height: 34, borderRadius: '50%', cursor: 'pointer', flexShrink: 0,
                background: RAIL_COLOR[p.progress], color: '#fff', border: 'none',
                fontFamily: font.ui, fontSize: 13, fontWeight: 800,
                boxShadow: phasePick === p.id ? `0 0 0 4px ${color.purple}44` : 'none',
              }}
            >
              {p.id}
            </button>
            {i < phases.length - 1 && (
              <div style={{ flex: 1, height: 2, background: RAIL_COLOR[phases[i + 1].progress] === '#c9c8d9' ? '#e5e7ef' : RAIL_COLOR[p.progress], margin: '0 8px' }} />
            )}
          </div>
        ))}
      </div>

      {/* Phase cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {phases.map((p) => {
          const active = phasePick === p.id;
          return (
            <div
              key={p.id}
              onClick={() => setPhasePick(p.id)}
              style={{
                background: color.surface, borderRadius: radius.card, padding: 16, cursor: 'pointer',
                border: `1px solid ${active ? color.purple : color.cardBorder}`,
                boxShadow: active ? `0 0 0 3px ${color.purple}22` : shadow.card,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 3 }}>
                <span
                  style={{
                    width: 22, height: 22, borderRadius: 7, background: RAIL_COLOR[p.progress], color: '#fff',
                    fontSize: 11, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  {p.id}
                </span>
                <span style={{ fontSize: 13.5, fontWeight: 800, color: color.ink }}>{p.title}</span>
              </div>
              <div style={{ fontSize: 12, color: color.textMuted, marginBottom: 12, paddingLeft: 32 }}>{p.statusLine}</div>

              <div style={{ fontSize: 11, fontWeight: 700, color: color.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                Captured artifacts
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {p.artifacts.map((a) => (
                  <ArtifactRow key={a.id} artifact={a} onDownload={() => onDownload(a)} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ArtifactRow({ artifact: a, onDownload }: { artifact: EngagementArtifact; onDownload: () => void }) {
  const chip = formatChip[a.format] || { bg: '#f1f2f8', fg: '#334155' };
  const can = isDownloadable(a.state);
  // Honest non-downloadable labels (task 1218070214136694, subtask 6): 'External' says "produced
  // outside Jarvis", not "not built yet"; Planned/Queued say "not generated".
  const notReadyLabel = a.state === 'External' ? 'External' : 'Not yet';
  const notReadyTitle = a.state === 'External' ? 'Produced outside Jarvis — not a Jarvis download' : 'Not generated yet';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px', borderRadius: radius.control, background: '#fbfbfe', border: `1px solid ${color.cardBorder}` }}>
      <span
        style={{
          background: chip.bg, color: chip.fg, borderRadius: 5, padding: '2px 6px',
          fontFamily: font.mono, fontSize: 11, fontWeight: 700, flexShrink: 0,
        }}
      >
        {a.format}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: color.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {a.name}
        </div>
        <div style={{ fontSize: 11, color: color.textMuted, marginTop: 1 }}>
          {[a.size_label, a.updated_label].filter(Boolean).join(' · ')}
        </div>
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, color: color.textMuted, flexShrink: 0 }}>{a.state}</span>
      <button
        onClick={(e) => { e.stopPropagation(); onDownload(); }}
        disabled={!can}
        title={can ? `Download ${a.name}` : notReadyTitle}
        style={{
          display: 'flex', alignItems: 'center', gap: 5, padding: '5px 9px', borderRadius: 8, flexShrink: 0,
          border: `1px solid ${can ? color.cardBorder : 'transparent'}`,
          background: can ? color.surface : 'transparent',
          color: can ? color.ink : '#b3b2c6',
          fontFamily: font.ui, fontSize: 11, fontWeight: 600,
          cursor: can ? 'pointer' : 'not-allowed',
        }}
      >
        {can ? <><Download size={11} /> Download</> : notReadyLabel}
      </button>
    </div>
  );
}
